/*
 * The Order page: a store's menu from the hub, in the app's groups and words, and the sheets that add to the order
 * (pick a store, when you want it, a size, filling a tub). Nothing here decides what's available or what it costs: the
 * hub's menu does. A read that fails says so ("We can't load Rosebery's menu right now."), never "sold out".
 * Each add is a hub line { storeId, productId, date, quantity, flavours?, parts? } sent as `ms:added` for the cart.
 */
import { hub, storeChoice, setStoreChoice, zoneFor, todayIn, signInUrl } from "./ms-hub.js";
import { money, timeLabel } from "./ms-format.js";
import { allows, allowsCake, saidIn, codesText, codesSaid, leaveLine, noneLeft, ordered, LEAVE_OUT } from "./ms-allergens.js";
import { tubParts, even, isEven, canSplit, splits, cuts, moveCut, stepCut, fractions } from "./ms-tub.js";
import { boxesOf, barsFor, trayWords, boxButton, boxLine, hasIt, chocStores, barCount } from "./ms-box.js";

// ── The rules (tested in tests/order.test.mjs) ────────────────────────────────────────────────────────────────────

/** What the menu read came to: the store's menu, the store not taking website orders, or a read that failed. */
export function menuState(result, storeName) {
  if (!result || !result.ok) {
    return { kind: "cant-load", message: `We can’t load ${storeName}’s menu right now.`, line: "It’s not you, and nothing’s sold out because of it." };
  }
  if (result.data && result.data.reason) return { kind: "store-off", reason: result.data.reason };
  return { kind: "menu", items: (result.data && result.data.items) || [] };
}

const todayOf = (it, date) => (it.days || []).find((d) => d.date === date) || null;
const soldOut = (it, date) => { const d = todayOf(it, date); return !d || !d.available; };
const SMALL = new Set(["a", "al", "an", "and", "de", "del", "in", "of", "on", "or", "the", "to", "with", "x"]);
/** "THE SLAB" as a person reads it: "The Slab". The Shopify title wins when the item has one. */
export const nameOf = (it) => it.title || String(it.name || "").toLowerCase().replace(/(^|[\s(.])([a-z][a-z'-]*)/g,
  (whole, p, word, at) => (at > 0 && p === " " && SMALL.has(word) ? whole : p + word[0].toUpperCase() + word.slice(1)));

function scoopCount(name) {
  const digits = /\d+/.exec(name);
  if (digits && Number(digits[0]) > 0) return Number(digits[0]);
  for (const [word, n] of [["single", 1], ["one", 1], ["double", 2], ["two", 2], ["triple", 3], ["three", 3]]) if (name.includes(word)) return n;
  return null;
}

/**
 * What the store makes at the counter, as the app reads it (Counter.swift): scoop products by how many (the baby scoop
 * first), the extra scoop that goes on top of the most, and the extra specials. A scoop name that says neither is left
 * out rather than guessed at. Flavours are chosen at the counter.
 */
export function counterMenu(items, date) {
  const scoops = [];
  let extraScoop = null;
  for (const it of items) {
    if (it.kind !== "scoop") continue;
    const name = String(it.name).toLowerCase();
    if (name.includes("extra") || name.startsWith("add")) { extraScoop = extraScoop || it; continue; }
    const baby = name.includes("baby");
    const count = baby ? 1 : scoopCount(name);
    if (!count) continue;
    scoops.push({ productId: it.productId, count, baby, priceCents: it.priceCents, soldOut: soldOut(it, date),
      title: baby ? "Baby scoop" : `${count} scoop${count === 1 ? "" : "s"}` });
  }
  scoops.sort((a, b) => (a.baby ? 0 : a.count) - (b.baby ? 0 : b.count));
  const prices = scoops.map((s) => s.priceCents);
  return {
    scoops, extraScoop,
    extras: items.filter((it) => it.kind === "extra").map((it) => ({ productId: it.productId, title: nameOf(it), priceCents: it.priceCents, soldOut: soldOut(it, date), imageUrl: it.imageUrl || null, handle: it.handle || null })),
    fromCents: prices.length ? Math.min(...prices) : null,
    hasScoops: scoops.some((s) => !s.soldOut),
  };
}

/** The lines for scoops: the size, and any more scoops on top of the most (the app's stepper, 0 to 5). */
export function scoopLines(storeId, date, option, more = 0, extraScoop = null) {
  const lines = [{ storeId, productId: option.productId, date, quantity: 1 }];
  if (more > 0 && extraScoop) lines.push({ storeId, productId: extraScoop.productId, date, quantity: Math.min(5, more) });
  return lines;
}

/** What the page still shows under a note when no store is picked yet: the groups that need no store (limited editions
 * go to their own pages, posted is posted), or null when the note stands alone. */
export function groupsWithoutStore(kind) {
  return kind === "no-store" || kind === "store-off" ? ["limited", "posted"] : null;
}

/** No store to order from yet: the empty order column steps aside and what's left (Limited edition, Posted) takes the width. */
export function noteWide(kind) {
  return kind === "no-store" || kind === "store-off" || kind === "cant-stores";
}

/** The Order group an address's #anchor points at: the group's own id, or the menu's short one (#tubs, #limited). */
export function groupFor(hash) {
  const h = String(hash || "").replace(/^#/, "");
  if (!h) return null;
  const SHORT = { tubs: "order-tubs", cakes: "order-cakes", chocolate: "order-chocolate", limited: "order-limited", specials: "order-specials", classics: "order-classics", merch: "order-merch", posted: "order-posted" };
  if (SHORT[h]) return SHORT[h];
  return /^order-(feature|specials|classics|tubs|cakes|chocolate|limited|merch|posted)$/.test(h) ? h : null;
}

/** The store sheet's line when it opens for a link to the classics (the old classics page, the menus) before a store's picked. */
export const storesSub = (hash) => (groupFor(hash) === "order-classics" ? "Classics and their allergens are your store’s own, so they show once you’ve picked one." : null);

/**
 * The store's menu in the page's groups: cakes, tubs and the store's merch (each product's sizes as one row). Only what
 * the menu lists is shown; price and sold out come from the menu alone.
 */
export function groupMenu(items, date) {
  const cakes = [], tubs = [], merch = new Map();
  for (const it of items) {
    if (it.kind === "cake") cakes.push({ productId: it.productId, title: nameOf(it), priceCents: it.priceCents, soldOut: soldOut(it, date), handle: it.handle || null, imageUrl: it.imageUrl || null, day: todayOf(it, date) });
    else if (it.kind === "tub") tubs.push({ productId: it.productId, name: it.name, priceCents: it.priceCents, soldOut: soldOut(it, date) });
    else if (it.kind === "merch") {
      const key = it.shopifyProductId || it.productId;
      const row = merch.get(key) || { key, title: nameOf(it), priceCents: it.priceCents, handle: it.handle || null, imageUrl: it.imageUrl || null, sizes: [] };
      row.priceCents = Math.min(row.priceCents, it.priceCents);
      row.sizes.push({ productId: it.productId, size: it.size, priceCents: it.priceCents, soldOut: soldOut(it, date) });
      merch.set(key, row);
    }
  }
  const rows = [...merch.values()].map((r) => ({ ...r, soldOut: r.sizes.every((s) => s.soldOut) }));
  return { cakes, tubs, merch: rows };
}

const handleOf = (name) => String(name).toLowerCase().replace(/\s+messina$/, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/**
 * The store on arrival: `?store=` (a hub id or a store's handle, so old order links land on the right store), else the
 * one remembered here. A store not taking website orders isn't chosen: the picker opens with it set apart. The header's
 * store pill on another page asks to change the store (`?change=store`), so the picker opens over the store you have.
 */
export function arrival(stores, remembered, asked, change = false) {
  const find = (k) => k && stores.find((s) => s.id === String(k) || handleOf(s.name) === String(k).toLowerCase());
  const want = find(asked) || (remembered && find(remembered.id));
  if (want && want.open && want.open.web) return { store: want, openPicker: change };
  // A store asked for (a link, or remembered) that isn't taking website orders is named, not silently dropped.
  return { store: null, openPicker: true, off: want || null };
}

const PLACES = { NSW: "new south wales sydney", VIC: "victoria melbourne", QLD: "queensland brisbane", WA: "western australia perth", SA: "south australia adelaide", TAS: "tasmania hobart", ACT: "australian capital territory canberra" };

/** The picker's two lists, matching a search on the store's name, its state, or the state's name or capital: taking
 *  orders, then not right now. */
/** A store's line in the picker. One that's open but not taking website orders says where you can still order. */
export function pickerLine(h, shut = false) {
  if (!h || h.openNow === null || h.openNow === undefined) return "";
  if (h.openNow && shut) return "Open now · order at the counter";
  if (h.openNow && h.today && h.today.closes) return `Open now · closes ${timeLabel(h.today.closes)}`;
  if (h.next) return `Opens ${timeLabel(h.next.opens)}${h.next.date === (h.today && h.today.date) ? "" : " another day"}`;
  return "Closed";
}

export function pickerGroups(stores, query) {
  const q = String(query || "").trim().toLowerCase();
  const hit = stores.filter((s) => !q || s.name.toLowerCase().includes(q) || String(s.state || "").toLowerCase() === q || (q.length > 2 && (PLACES[s.state] || "").includes(q)));
  const byName = (a, b) => a.name.localeCompare(b.name);
  return { open: hit.filter((s) => s.open && s.open.web).sort(byName), shut: hit.filter((s) => !(s.open && s.open.web)).sort(byName) };
}

/** Open stores in states, the customer's own state first and the rest in order; each state's stores by name. */
export function byState(stores, home) {
  const states = [...new Set(stores.map((s) => s.state || ""))].sort((a, b) => (a === home ? -1 : b === home ? 1 : a.localeCompare(b)));
  return states.map((state) => ({ state, stores: stores.filter((s) => (s.state || "") === state) }));
}

/** When a pick-up can be: ASAP first ("ASAP · about 1:15pm"), then the times later today, and the store's last pick-up. */
export function timeOptions(pt, storeName, zone) {
  const last = timeLabel(pt.lastAt, zone);
  if (!pt.open) {
    return { tooLate: true, options: [], message: `${storeName}’s last pick-up today was ${last}. Tubs are today only, so try another store or come back tomorrow.` };
  }
  return {
    tooLate: false,
    options: [{ asap: true, at: null, label: `ASAP · about ${timeLabel(pt.firstAt, zone)}` }, ...pt.times.map((at) => ({ asap: false, at, label: timeLabel(at, zone) }))],
    note: `${storeName}’s last pick-up today is ${last}.`,
  };
}

/** A tub being filled when the store changes: the same size at the new store, keeping the flavours it has, or null when
 *  that store doesn't sell it today. */
export function carryTub(tub, tubs, flavours) {
  if (!tub) return null;
  const t = tubs.find((x) => x.name === tub.name && !x.soldOut);
  if (!t) return null;
  const have = new Set([...((flavours && flavours.specials) || []), ...((flavours && flavours.classics) || [])].map((f) => f.id));
  return { productId: t.productId, picked: tub.picked.filter((id) => have.has(id)) };
}

/** The time pill: Closed for today once the last pick-up has gone, otherwise the chosen time (ASAP with its time). */
export function timePill(t, time) {
  if (t && t.tooLate) return { label: "Closed for today", closed: true };
  if (t && time.asap) return { label: t.options[0].label, closed: false };
  return { label: time.label, closed: false };
}

/** The pick-up time kept for this store and day (the cart's own record), while the store still offers it; else ASAP. */
export function keptTime(t, kept, storeId, date) {
  const at = kept && kept[`${storeId}|${date}`];
  const o = t && !t.tooLate && at ? t.options.find((x) => !x.asap && x.at === at) : null;
  if (o) return { asap: false, at: o.at, label: o.label };
  return { asap: true, at: null, label: t && !t.tooLate ? t.options[0].label : "ASAP" };
}

const DAY_SHORT = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
/** The days a tub can be for: the store's next seven that sell that size (the menu's days), today first, as the app's
 *  DateStrip says them. */
export function tubDays(item, today) {
  return (item && item.days ? item.days : []).filter((d) => d.available && d.date >= today).slice(0, 7).map((d) => {
    const [y, m, n] = d.date.split("-").map(Number);
    return { date: d.date, d: d.date === today ? "TODAY" : DAY_SHORT[new Date(Date.UTC(y, m - 1, n)).getUTCDay()], n };
  });
}

/** A day's pick-up times for a tub: today's lead with ASAP (timeOptions); another day lists its times, the first picked. */
export function dayTimes(pt, storeName, zone, isToday) {
  if (isToday) return timeOptions(pt, storeName, zone);
  if (!pt.open || !pt.times.length) return { tooLate: true, options: [], message: `${storeName} has no pick-up times that day.` };
  return { tooLate: false, options: pt.times.map((at) => ({ asap: false, at, label: timeLabel(at, zone) })), note: `${storeName}’s last pick-up that day is ${timeLabel(pt.lastAt, zone)}.` };
}

/** A tub's flavour list: the specials, then the classics folded behind one row until asked for (or one of them is picked). */
export function tubGroups(f, picked, expanded) {
  const open = expanded || !f.specials.length || f.classics.some((x) => picked.includes(x.id));
  return { specials: f.specials, classics: open ? f.classics : [], folded: open ? 0 : f.classics.length };
}

/** A tub goes in with one flavour at least and no more than its size takes. */
export const tubReady = (picked, max) => picked.length >= 1 && picked.length <= max;
/** A tub's hub line: its flavours and, when it isn't even, how much of each (the app's split). A tub for later goes in
 *  with none: its flavours are chosen at the counter, so it's packed fresh. */
export function tubLine(storeId, date, tub, flavours, parts, later = false) {
  const line = { storeId, productId: tub.productId, date, quantity: 1, flavours: later ? [] : [...flavours] };
  const p = later ? undefined : tubParts(parts);
  return p ? { ...line, parts: p } : line;
}

/** Whether a tub is for later: any day but today, or a set time today. Only an ASAP tub today is filled now (the app's rule). */
export const tubLater = (date, today, time) => date !== today || !(time && time.asap);

/** The tub sheet's button, one line at any width: "Add · ASAP · $35.90". */
export const tubAddLabel = (when, cents) => `Add · ${when} · ${money(cents)}`;

/** How you get it, said one way on the card, the phone's bar, the drawer and the cart. */
export const howWords = (storeName, delivery) => {
  const where = delivery ? "Delivered from" : "Pick-up from";
  return { where, short: `${where} ${storeName}` };
};

/** The flavours left on the menu while things are left out (the app's Leave out): a flavour whose codes couldn't be read
 *  never shows while filtering. `shown` of `total` is the line under the jumps ("27 of 48 flavours"). */
export function leaveMenu(f, leaving) {
  if (!f) return { specials: [], classics: [], shown: 0, total: 0 };
  const keep = (x) => allows(x.allergens || [], Array.isArray(x.allergens), leaving);
  const specials = (f.specials || []).filter(keep), classics = (f.classics || []).filter(keep);
  return { specials, classics, shown: specials.length + classics.length, total: (f.specials || []).length + (f.classics || []).length };
}

const WEEKDAY = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
/** A day as the tub sheet says it: "Today", "Tomorrow", or its weekday ("Sunday"). */
export function dayWord(date, today) {
  if (date === today) return "Today";
  const [y, m, d] = date.split("-").map(Number), [ty, tm, td] = today.split("-").map(Number);
  const days = Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ty, tm - 1, td)) / 86400000);
  return days === 1 ? "Tomorrow" : WEEKDAY[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
}

/** When a tub is for, on its button: "ASAP", "1pm" later today, "Sunday at 1pm". */
export function tubWhen(date, today, time) {
  if (date === today) return time && !time.asap ? time.label : "ASAP";
  return `${dayWord(date, today)}${time && time.label ? ` at ${time.label}` : ""}`;
}

/** "Show 27 flavours", "Show 27 flavours and 1 cake" (the app's words): what Leave out's button will leave on the page. */
export function showLabel(flavours, cakes) {
  const f = `${flavours} flavour${flavours === 1 ? "" : "s"}`;
  return cakes ? `Show ${f} and ${cakes} cake${cakes === 1 ? "" : "s"}` : `Show ${f}`;
}

/** A day chip's full name for screen readers ("Today, Friday 23 October", "Saturday 24 October"); its face says SAT 24. */
export function dayLong(date, today) {
  const [y, m, d] = date.split("-").map(Number);
  const words = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" }).replace(",", "");
  return date === today ? `Today, ${words}` : words;
}

/** A tub's button, on one line: "Add · ASAP · $35.90", "Add · 1pm · $35.90", or "Add for Sunday · $35.90" for another
 *  day, whose time is in the sheet above it. Delivered tubs are ASAP. */
export function tubButton({ date, today, time, cents, delivery = false }) {
  if (delivery || date === today) return tubAddLabel(delivery ? "ASAP" : tubWhen(date, today, time), cents);
  const day = dayWord(date, today);
  return `Add for ${day === "Tomorrow" ? "tomorrow" : day} · ${money(cents)}`;
}

/** The phone bar's two lines: the store alone, so it never cuts off (its icon, a map pin or a truck, says the way in),
 *  over the time, short ("ASAP · 12:15pm"; delivered, "ASAP today"). */
export function stripWords(delivery, storeName, label) {
  const when = delivery ? "ASAP today" : String(label || "ASAP").replace(" · about ", " · ");
  const way = `${delivery ? "Delivery" : "Pick-up"} from ${storeName || "your store"}`;
  return { where: storeName || "Pick a store", when: delivery ? "Delivered ASAP" : when, icon: delivery ? "truck" : "map-pin", aria: `How you get it: ${way}, ${when.replace(" · ", ", ")}. Change` };
}

// ── The page (browser only) ───────────────────────────────────────────────────────────────────────────────────────

const TIMES_KEY = "ms-hub-pickup-times-v1";
const keptTimes = () => { try { return JSON.parse(sessionStorage.getItem(TIMES_KEY) || "{}"); } catch { return {}; } };
const DELIVERY_KEY = "ms-hub-delivery-v1";
const LEAVE_KEY = "ms-leave-out-v1";
const STATE_NAMES = { NSW: "New South Wales", VIC: "Victoria", ACT: "ACT", QLD: "Queensland", SA: "South Australia", WA: "Western Australia", TAS: "Tasmania", NT: "Northern Territory" };
const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const tubLabel = (name) => String(name).replace(/\s*tub$/i, "");
const readLeaving = () => { try { return ordered(JSON.parse(localStorage.getItem(LEAVE_KEY) || "[]")); } catch { return []; } };
const keepLeaving = (l) => { try { localStorage.setItem(LEAVE_KEY, JSON.stringify(l)); } catch { /* this visit only */ } };
const CAKE_WORDS = ["dairy", "eggs", "gluten", "gelatine", "nuts", "soy", "alcohol"];
const andList = (w) => (w.length > 1 ? `${w.slice(0, -1).join(", ")} and ${w.at(-1)}` : w[0]);
const NUMBER_WORDS = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];

if (typeof window !== "undefined" && typeof customElements !== "undefined") {
  class MsOrderMenu extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.cfg = JSON.parse(this.querySelector("script[data-order-config]")?.textContent || "{}");
      this.icons = document.getElementById("ms-order-icons");
      this.time = { asap: true, at: null, label: "ASAP" };
      this.leaving = readLeaving();
      this.signedIn = document.documentElement.dataset.signedIn === "1";
      this.addEventListener("click", (e) => this.onClick(e));
      document.addEventListener("click", (e) => this.onSheetClick(e));
      document.addEventListener("input", (e) => { if (e.target.matches("[data-store-search]")) this.renderPicker(e.target.value); });
      document.addEventListener("ms:retry", (e) => { if (this.contains(e.target)) this.load(); });
      document.addEventListener("ms:track", (e) => this.onTrack(e));
      document.addEventListener("ms:sheet-open", (e) => { if (e.target.id === "order-leave") this.openLeave(); });
      document.addEventListener("ms:hold-refused", (e) => this.boxRefused(e.detail));
      document.addEventListener("keydown", (e) => this.onKey(e));
      document.addEventListener("pointerdown", (e) => this.onPointer(e));
      if (this.signedIn) this.readSaved();
      this.start();
    }

    icon(name) { return this.icons?.content.querySelector(`[data-i="${name}"]`)?.innerHTML || ""; }
    $(sel) { return this.querySelector(sel); }
    all(sel) { return document.querySelectorAll(sel); }
    sheet(id) { return document.getElementById(id); }
    open(id) { this.sheet(id)?.open?.(); }
    close(id) { this.sheet(id)?.close?.(); }

    /** A signed-in customer's Leave out is kept on their account (the app's): it wins over this browser's. */
    async readSaved() {
      const r = await hub("me", {});
      if (!r.ok || !r.data) return;
      this.saved = Array.isArray(r.data.leaveOut);
      if (this.saved) { this.leaving = ordered(r.data.leaveOut); keepLeaving(this.leaving); if (this.items) this.render(); }
    }

    async start() {
      const r = await hub("stores", {});
      if (!r.ok) { this.showNote("cant-stores"); return; }
      this.stores = r.data.stores || [];
      const url = new URL(window.location.href);
      const change = url.searchParams.get("change") === "store";
      if (change) { url.searchParams.delete("change"); history.replaceState(history.state, "", url); }
      const a = arrival(this.stores, storeChoice(), url.searchParams.get("store"), change);
      this.renderPicker("");
      if (a.store) { this.choose(a.store); if (a.openPicker) this.open("order-stores"); }
      else if (a.off) { this.all("[data-store-name]").forEach((n) => (n.textContent = a.off.name)); this.showNote("store-off"); this.open("order-stores"); }
      else {
        this.showNote("no-store");
        const sub = document.querySelector("[data-stores-sub]");
        if (sub) { sub.textContent = storesSub(location.hash) || ""; sub.parentElement.hidden = !sub.textContent; }
        this.open("order-stores");
      }
    }

    choose(store, picked) {
      // A store picked from the sheet changes the whole menu, so the page goes back to its top, the new store's name.
      if (picked && this.store && this.store.id !== store.id) scrollTo({ top: 0 });
      // A tub being filled goes with the change: the same size at the new store, once its menu is in.
      const tubSheet = document.getElementById("order-tub");
      this.carry = tubSheet && !tubSheet.hidden && this.tub ? { name: this.tub.name, picked: this.tubPicked } : null;
      this.store = store;
      this.zone = zoneFor(store.state);
      this.date = todayIn(this.zone);
      setStoreChoice({ id: store.id, name: store.name, state: store.state });
      // The sheets sit outside this element, so their store names are filled page-wide.
      this.all("[data-store-name]").forEach((n) => (n.textContent = store.name));
      this.renderStrip();
      this.time = { asap: true, at: null, label: "ASAP" };
      this.renderTime();
      this.renderPicker(document.querySelector("[data-store-search]")?.value || "");
      this.load();
    }

    showNote(kind) {
      const box = this.$("[data-order-state]"), groups = this.$("[data-order-groups]");
      const keep = groupsWithoutStore(kind);
      groups.hidden = kind !== "menu" && !keep;
      if (keep) groups.querySelectorAll("[data-group]").forEach((n) => (n.hidden = !keep.includes(n.dataset.group)));
      box.hidden = kind === "menu";
      box.querySelectorAll("[data-state]").forEach((n) => (n.hidden = n.dataset.state !== kind));
      this.classList.toggle("wide", noteWide(kind));
      const key = this.$("[data-codes-key]");
      if (key) key.hidden = kind !== "menu";
    }

    async load() {
      this.showNote("loading");
      const store = this.store;
      const [menu, flavours, times, bars] = await Promise.all([
        hub("menu", { storeId: store.id }), hub("flavours", { storeId: store.id }), hub("pickup-times", { storeId: store.id, date: this.date }), hub("bars", {}),
      ]);
      if (store !== this.store) return;
      const state = menuState(menu, store.name);
      if (this.carry && (state.kind === "cant-load" || state.kind === "store-off")) { this.close("order-tub"); this.carry = null; }
      if (state.kind === "cant-load") {
        const msg = this.$('[data-state="cant-load"] [data-message]');
        if (msg) msg.textContent = state.message;
        this.showNote("cant-load");
        return;
      }
      if (state.kind === "store-off") { this.showNote("store-off"); this.open("order-stores"); return; }
      this.items = state.items;
      this.flavours = flavours.ok ? flavours.data : null;
      this.across = bars.ok && bars.data && Array.isArray(bars.data.stores) ? bars.data : null;
      this.times = times.ok ? timeOptions(times.data, store.name, this.zone) : null;
      this.dayTimes = { [this.date]: this.times };
      this.closed = !!(this.times && this.times.tooLate);
      this.time = keptTime(this.times, keptTimes(), store.id, this.date);
      this.render();
      this.renderTime();
      this.showNote("menu");
      this.renderJump();
      if (!this.hashDone) {
        this.hashDone = true;
        const g = document.getElementById(groupFor(location.hash) || "");
        if (g && !g.hidden) g.scrollIntoView({ block: "start" });
      }
      this.openAsked();
      if (this.toChoc) {
        this.toChoc = false;
        const h = this.$("[data-choc-h]");
        if (h && !this.$("[data-group='chocolate']").hidden) { h.scrollIntoView({ block: "start" }); h.focus({ preventScroll: true }); }
      }
      const carry = carryTub(this.carry, this.pricedTubs(), this.flavours);
      if (carry) this.openTub(carry.productId, carry.picked);
      else if (this.carry) this.close("order-tub");
      this.carry = null;
    }

    /** A flavour's codes as the app writes them, read aloud in words; one we can't read says to ask. */
    codesHtml(f) {
      if (!Array.isArray(f.allergens)) return `<div class="codes ask">Allergens: ask at the counter</div>`;
      const t = codesText(f.allergens);
      return t ? `<div class="codes"><span aria-hidden="true">${esc(t)}</span><span class="sr">${esc(codesSaid(f.allergens) || "")}</span></div>` : "";
    }

    render() {
      const g = groupMenu(this.items, this.date);
      const c = counterMenu(this.items, this.date);
      this.counter = c;
      this.groups = g;
      const tubs = this.pricedTubs();
      const leaving = this.leaving.length > 0;
      const show = (sel, on) => { const n = this.$(sel); if (n) n.hidden = !on; };
      const none = (k, text) => { const n = this.$(`[data-none="${k}"]`); if (n) { n.hidden = !text; n.textContent = text || ""; } };
      // The extra specials (Mr Messina XXL), as the cocoa feature. Its allergens aren't on the till, so it steps aside
      // while something's left out.
      const words = this.$("[data-feature-words]")?.innerHTML.trim() || "";
      this.$("[data-feature]").innerHTML = c.extras.map((x) => {
        const addBtn = x.soldOut ? "" : `<button class="circle add-c" type="button" data-add-extra="${esc(x.productId)}" aria-label="Add ${esc(x.title)}, ${money(x.priceCents)}"${this.delivery ? " hidden" : ""}>${this.icon("plus")}</button>`;
        return `
        <div class="g-cocoa feature"><div class="stk" style="--r: var(--ms-web-radius-card); --tilt: -1deg; --drop: var(--ms-color-burnt-orange)"><div class="face feature-face">
          <div class="stack feature-tx"><h2 class="t-title">${esc(x.title)}</h2>${words ? `<p class="t-body">${words}</p>` : ""}
            <p class="t-card feature-price">${money(x.priceCents)}</p><div class="codes ask">Allergens: ask at the counter</div>${x.soldOut ? this.soldPill() : `<span class="status" data-pickup-only${this.delivery ? "" : " hidden"}>Pick-up only</span>`}${x.imageUrl ? "" : addBtn}</div>
          ${x.imageUrl ? `<div class="feature-pic"><img src="${esc(x.imageUrl)}" alt="" loading="lazy" width="640" height="640">${addBtn}</div>` : ""}
        </div></div></div>`;
      }).join("");
      show("[data-group='feature']", c.extras.length > 0 && !leaving);
      if (c.extras[0]) this.$("[data-group='feature']").dataset.label = c.extras[0].title;
      const left = leaveMenu(this.flavours, this.leaving);
      const canAdd = c.hasScoops || tubs.some((t) => !t.soldOut);
      const add = (f) => (canAdd ? `<button class="circle add-c" type="button" data-add-special="${esc(f.id)}" aria-haspopup="dialog" aria-label="Add ${esc(f.title)}">${this.icon("plus")}</button>` : "");
      // This month's specials: the app's sticker cards, square, the add circle on the photo's corner.
      const specials = (this.flavours && this.flavours.specials) || [];
      this.$("[data-specials]").innerHTML = left.specials.map((f) => `
        <div class="card sp-card"><div class="stk"><div class="face">${f.imageUrl ? `<img src="${esc(f.imageUrl)}" alt="" loading="lazy" width="600" height="600">` : ""}</div>${add(f)}</div>
          <div class="bd"><div class="t-card">${esc(f.title)}</div>${f.description ? `<div class="sub">${esc(f.description)}</div>` : ""}${this.codesHtml(f)}</div></div>`).join("");
      show("[data-group='specials']", specials.length > 0 && canAdd);
      none("specials", specials.length && !left.specials.length ? noneLeft("this month’s specials", this.leaving) : null);
      // The classics, every one: lists load as you scroll, never behind a button.
      const classics = (this.flavours && this.flavours.classics) || [];
      this.$("[data-classics]").innerHTML = left.classics.map((f) => `
        <div class="mrow cl-row"><div class="dot">${f.imageUrl ? `<img src="${esc(f.imageUrl)}" alt="" loading="lazy" width="128" height="128">` : ""}</div>
          <div class="tx"><div class="t-card">${esc(f.title)}</div>${f.description ? `<div class="sub">${esc(f.description)}</div>` : ""}${this.codesHtml(f)}</div>${add(f)}</div>`).join("");
      show("[data-group='classics']", classics.length > 0 && canAdd);
      none("classics", classics.length && !left.classics.length ? noneLeft("the classics", this.leaving) : null);
      show("[data-flavours-fail]", !this.flavours);
      const spLead = this.$("[data-group='specials'] .sec-hd .lead"), tubsWords = this.$("[data-specials-lead-tubs]");
      if (spLead && tubsWords) { spLead.dataset.all ??= spLead.textContent; spLead.textContent = c.hasScoops ? spLead.dataset.all : tubsWords.innerHTML.trim(); }
      // Tubs: a button for each size the store sells, at the menu's price.
      this.$("[data-tubs]").innerHTML = tubs.map((t) => `
        <button class="tub-size" type="button" data-tub="${esc(t.productId)}" aria-haspopup="dialog"${t.soldOut ? " disabled" : ""} aria-label="Fill a ${esc(tubLabel(t.name))} tub, ${money(t.priceCents)}, ${t.soldOut ? "sold out here" : `up to ${t.maxFlavours} flavours`}">
          <span class="t-title">${esc(tubLabel(t.name))}</span><span class="tub-price">${money(t.priceCents)}</span>
          <span class="t-small">${t.soldOut ? "Sold out here" : `Up to ${t.maxFlavours} flavours`}</span><span class="ts-add" aria-hidden="true">${this.icon("plus")}</span></button>`).join("");
      show("[data-group='tubs']", tubs.length > 0);
      this.renderChoc();
      // Cakes: the Shopify collection's, each with what its page says it has; off the page while it has what's left out.
      let cakesLeft = 0, cakesAll = 0;
      this.querySelectorAll(".cake-item").forEach((n) => {
        const has = saidIn(n.dataset.said);
        const words = has ? CAKE_WORDS.filter((w) => has.has(w === "nuts" ? "nuts" : w)) : [];
        const box = n.querySelector("[data-cake-codes]");
        if (box) box.textContent = words.length ? `Contains ${andList(words)}` : "";
        const ok = allowsCake(has, this.leaving);
        n.hidden = !ok; cakesAll += 1; cakesLeft += ok ? 1 : 0;
      });
      none("cakes", cakesAll && !cakesLeft ? noneLeft("our cakes", this.leaving) : null);
      this.cakesLeft = cakesLeft;
      // Limited editions have no codes we can check: they step aside while something's left out, as the XXL does.
      show("[data-group='limited']", !leaving);
      // The store's merch, picked up with the order.
      this.$("[data-merch]").innerHTML = g.merch.map((m) => `
        <div class="mrow"><div class="dot">${m.imageUrl ? `<img src="${esc(m.imageUrl)}" alt="" loading="lazy" width="152" height="152">` : ""}</div>
          <div class="tx"><div class="t-card">${esc(m.title)}</div><div class="sub">${m.soldOut ? "Sold out here" : money(m.priceCents)}</div></div>
          ${m.soldOut ? "" : `<button class="circle add-c" type="button" data-add-merch="${esc(m.key)}" aria-label="Add ${esc(m.title)}">${this.icon("plus")}</button>`}</div>`).join("");
      show("[data-group='merch']", g.merch.length > 0);
      if (this.cfg.only === "tubs") { for (const k of ["feature", "specials", "classics", "cakes", "chocolate", "limited", "merch", "posted"]) show(`[data-group='${k}']`, false); }
      const late = this.$("[data-late]");
      if (late) { late.hidden = !this.closed; const m = late.querySelector("[data-late-message]"); if (m && this.closed) m.textContent = this.times.message; }
      if (this.closed) this.querySelectorAll("[data-add-extra], [data-add-special], [data-tub], [data-box], [data-add-merch]").forEach((b) => (b.disabled = true));
      this.renderLeaveLine(left);
      this.renderFulfilment();
      this.renderJump();
    }

    /** Under the jumps while something's left out: what, how many flavours are left, what steps aside. */
    renderLeaveLine(left) {
      const on = this.leaving.length > 0;
      const line = this.$("[data-leave-line]");
      if (line) line.hidden = !on;
      const btn = this.$("[data-leave-btn]"), count = this.$("[data-leave-count]");
      if (btn) { btn.classList.toggle("on", on); btn.setAttribute("aria-label", on ? `Leave out, ${this.leaving.length} on` : "Leave out"); }
      if (count) { count.hidden = !on; count.textContent = String(this.leaving.length); }
      if (!on) return;
      const w = this.$("[data-leave-words]"), l = this.$("[data-leave-left]"), x = this.$("[data-leave-clear]");
      const words = leaveLine(this.leaving);
      if (w) w.textContent = words;
      if (x) x.setAttribute("aria-label", `Stop ${words.charAt(0).toLowerCase() + words.slice(1)}`);
      if (l && left) l.textContent = `${left.shown} of ${left.total} flavours`;
    }

    /** The Leave out sheet opens on what's left out now; its button says what it'll leave on the page. */
    openLeave() {
      this.draft = [...this.leaving];
      const save = document.querySelector("[data-leave-save]");
      if (save) {
        save.innerHTML = this.signedIn
          ? `<label class="set-row al-save"><span class="how-ic">${this.icon("check")}</span><span class="how-tx sr-tx"><span class="t-card">Save this for later</span><span class="t-small t-soft" id="al-keep-line">Kept on your account, so ordering opens this way next time, here and in the app.</span></span><button aria-describedby="al-keep-line" class="sw${this.saved !== false ? " on" : ""}" type="button" role="switch" aria-checked="${this.saved !== false}" aria-label="Save this for later" data-leave-keep><i></i></button></label>`
          : `<a class="set-row al-save" href="${esc(signInUrl(location.pathname))}"><span class="how-ic">${this.icon("check")}</span><span class="how-tx sr-tx"><span class="t-card">Save this for later</span><span class="t-small t-soft">Sign in to keep these on your account.</span></span>${this.icon("chevron-down")}</a>`;
      }
      this.renderLeaveSheet();
    }

    renderLeaveSheet() {
      this.all("[data-leave]").forEach((b) => { const on = this.draft.includes(b.dataset.leave); b.classList.toggle("on", on); b.setAttribute("aria-checked", String(on)); });
      const left = leaveMenu(this.flavours, this.draft);
      let cakes = 0;
      this.querySelectorAll(".cake-item").forEach((n) => { if (allowsCake(saidIn(n.dataset.said), this.draft)) cakes += 1; });
      const label = document.querySelector("[data-leave-show-label]");
      if (label) label.textContent = this.draft.length ? showLabel(left.shown, cakes) : "Show the whole menu";
    }

    async applyLeave(list) {
      this.leaving = ordered(list);
      keepLeaving(this.leaving);
      if (this.items) this.render();
      const keep = document.querySelector("[data-leave-keep]");
      if (this.signedIn && keep) {
        const on = keep.getAttribute("aria-checked") === "true";
        this.saved = on;
        await hub("allergens", { leaveOut: on && this.leaving.length ? this.leaving : null });
      }
    }

    /** Pick-up or Delivery, from either copy of the switch (Your order's head, the phone's sheet), or a tub's size. */
    onTrack(e) {
      if (e.target.matches("[data-tub-sizes] .track")) { this.changeSize(e.detail.index); return; }
      if (!e.target.matches('[data-name="fulfilment"]')) return;
      const delivery = e.detail.index === 1;
      try { localStorage.setItem(DELIVERY_KEY, delivery ? "1" : "0"); } catch { /* not remembered */ }
      this.setTracks(delivery);
      this.renderMode(delivery);
    }

    setTracks(delivery) {
      this.all('[data-name="fulfilment"]').forEach((t) => t.querySelectorAll('[role="radio"]').forEach((r, i) => { const on = (i === 1) === delivery; r.setAttribute("aria-checked", String(on)); r.classList.toggle("on", on); r.tabIndex = on ? 0 : -1; }));
    }

    /** Delivery is offered only where the store delivers; the choice is remembered for next time. */
    renderFulfilment() {
      const can = !!(this.store && this.store.open && this.store.open.delivery);
      this.all("[data-fulfil]").forEach((w) => (w.hidden = !can));
      const lead = (k, on) => { const n = this.$(`[data-lead-${k}]`); if (n) n.hidden = !on; };
      lead("delivery", can); lead("pickup", !can || !this.$("[data-lead-delivery]"));
      let want = false;
      try { want = can && localStorage.getItem(DELIVERY_KEY) === "1"; } catch { /* pick-up */ }
      this.setTracks(want);
      this.renderMode(want);
    }

    renderMode(delivery) {
      this.all("[data-how-time]").forEach((n) => (n.hidden = delivery));
      this.all("[data-delivery-line]").forEach((n) => (n.hidden = !delivery));
      this.all("[data-how-where]").forEach((n) => (n.textContent = howWords("", delivery).where));
      this.delivery = delivery;
      this.querySelectorAll("[data-add-extra]").forEach((b) => (b.hidden = delivery));
      this.querySelectorAll("[data-pickup-only]").forEach((n) => (n.hidden = !delivery));
      if (this.items) this.renderChoc();
      this.renderStrip();
      // Your order says so when the switch disagrees with what's held.
      document.dispatchEvent(new CustomEvent("ms:mode", { detail: { delivery } }));
    }

    /** Tub sizes from the flavours read (how many flavours each takes) at the menu's price, fewest flavours first. */
    pricedTubs() {
      const sizes = (this.flavours && this.flavours.tubs) || [];
      const priced = new Map(groupMenu(this.items, this.date).tubs.map((t) => [t.productId, t]));
      return sizes.filter((t) => priced.has(t.productId)).map((t) => ({ ...priced.get(t.productId), maxFlavours: t.maxFlavours, name: t.name }))
        .sort((a, b) => a.maxFlavours - b.maxFlavours);
    }

    soldPill() { return `<span class="status out">Sold out here</span>`; }

    /** The phone bar's strip: "Pick-up, Rosebery" over "ASAP · 12:15pm". */
    renderStrip() {
      const w = stripWords(this.delivery, this.store && this.store.name, timePill(this.times, this.time || { asap: true }).label);
      this.all("[data-strip-where]").forEach((n) => (n.textContent = w.where));
      this.all("[data-strip-when]").forEach((n) => (n.textContent = w.when));
      this.all("[data-strip]").forEach((n) => n.setAttribute("aria-label", w.aria));
      this.all("[data-strip-ic]").forEach((n) => (n.innerHTML = this.icon(w.icon)));
    }
    statusPill(t) { return `<span class="status good">${esc(t)}</span>`; }

    renderJump() {
      const nav = this.$("[data-jump]");
      if (!nav) return;
      const groups = [...this.querySelectorAll("[data-group]")].filter((g) => !g.hidden && g.id);
      nav.innerHTML = groups.map((g, i) => `<a href="#${g.id}" class="${i === 0 ? "on" : ""}"${i === 0 ? ' aria-current="true"' : ""}>${esc(g.dataset.label)}</a>`).join("");
      nav.dispatchEvent(new CustomEvent("ms:jump", { bubbles: true }));
    }

    renderPicker(query) {
      const list = document.querySelector("[data-store-list]");
      if (!list || !this.stores) return;
      const g = pickerGroups(this.stores, query);
      const row = (s, shut) => `<button class="pick-row${this.store && this.store.id === s.id ? " on" : ""}${shut ? " shut" : ""}" type="button" data-pick-store="${esc(s.id)}"${this.store && this.store.id === s.id ? ' aria-current="true"' : ""}${shut ? " disabled" : ""}>
        <span style="flex: 1; min-width: 0; text-align: left"><span class="t-card" style="display: block">${esc(s.name)}</span>
        <span class="t-small t-soft" style="display: block">${pickerLine(s.hours, shut)}</span></span>
        ${this.store && this.store.id === s.id ? this.icon("check") : ""}</button>`;
      const home = (this.store && this.store.state) || (storeChoice() || {}).state || null;
      const groups = byState(g.open, home);
      list.innerHTML = groups.map((x) => (groups.length > 1 ? `<div class="t-label pick-hd" role="heading" aria-level="3">${esc(STATE_NAMES[x.state] || x.state)}</div>` : "") + x.stores.map((s) => row(s, false)).join("")).join("")
        + (g.shut.length ? `<div class="t-label pick-hd" role="heading" aria-level="3">Not taking website orders right now</div>${g.shut.map((s) => row(s, true)).join("")}` : "")
        + (g.open.length + g.shut.length === 0 ? `<p class="t-small t-soft">No Messina by that name. Try the store’s name, a city or a state, like Melbourne or NSW.</p>` : "");
    }

    renderTime() {
      const label = () => { this.all("[data-time-label]").forEach((n) => (n.textContent = timePill(this.times, this.time).label)); this.renderStrip(); };
      label();
      const list = document.querySelector("[data-time-list]");
      if (!list) return;
      const t = this.times;
      const sub = document.querySelector("[data-time-sub]");
      if (sub && this.store) sub.textContent = `${this.store.name}, today. A tub takes about 15 minutes to pack.`;
      if (!t) { list.innerHTML = `<p class="t-small t-soft">We can’t check ${esc(this.store ? this.store.name : "the store")}’s pick-up times right now. ASAP still works.</p>`; return; }
      if (t.tooLate) { list.innerHTML = `<p class="t-body">${esc(t.message)}</p>`; return; }
      const on = (o) => (o.asap && this.time.asap) || (!o.asap && o.at === this.time.at);
      const opt = (o) => `<button class="pick-row${on(o) ? " on" : ""}" type="button" role="radio" aria-checked="${on(o)}" data-pick-time="${o.asap ? "asap" : esc(o.at)}">
        <span class="t-card" style="flex: 1; text-align: left">${esc(o.label)}</span>${on(o) ? this.icon("check") : ""}</button>`;
      list.innerHTML = `<div class="pick-list" role="radiogroup" aria-label="Pick-up time">${opt(t.options[0])}${t.options.length > 1 ? `<div class="t-label pick-hd" role="heading" aria-level="3">Later today</div>${t.options.slice(1).map(opt).join("")}` : ""}</div>`
        + `<p class="t-small t-soft" style="margin: 6px 0 0">${esc(t.note)}</p>`;
      if (this.time.asap) this.time.label = t.options[0].label;
      label();
      document.dispatchEvent(new CustomEvent("ms:asap", { detail: { storeId: this.store.id, date: this.date, label: t.options[0].label } }));
    }

    onClick(e) {
      const b = e.target.closest("button, a");
      if (!b) return;
      if (b.matches("[data-add-extra]")) { const x = this.counter.extras.find((y) => y.productId === b.dataset.addExtra); this.add([{ storeId: this.store.id, productId: b.dataset.addExtra, date: this.date, quantity: 1 }], x?.title, "extra", { imageUrl: x?.imageUrl || null }); }
      else if (b.matches("[data-add-special]")) this.openSizes(b.dataset.addSpecial);
      else if (b.matches("[data-tub]")) this.openTub(b.dataset.tub, []);
      else if (b.matches("[data-box]")) { if (b.getAttribute("aria-disabled") !== "true") this.openBox(b.dataset.box, [], b); }
      else if (b.matches("[data-add-merch]")) this.addMerch(b.dataset.addMerch);
      else if (b.matches("[data-leave-clear]")) this.applyLeave([]);
    }

    onSheetClick(e) {
      const sw = e.target.closest(".sw[data-leave], .sw[data-leave-keep]");
      if (sw) {
        e.preventDefault();
        if (sw.matches("[data-leave-keep]")) { const on = sw.getAttribute("aria-checked") !== "true"; sw.setAttribute("aria-checked", String(on)); sw.classList.toggle("on", on); return; }
        const id = sw.dataset.leave;
        this.draft = this.draft.includes(id) ? this.draft.filter((x) => x !== id) : [...this.draft, id];
        this.renderLeaveSheet();
        return;
      }
      const tday = e.target.closest("[data-tub-day]");
      if (tday) { this.pickTubDay(tday.dataset.tubDay); return; }
      const ttime = e.target.closest("[data-tub-time]");
      if (ttime) { const o = (this.tubTimes()?.options || []).find((x) => (ttime.dataset.tubTime === "asap" ? x.asap : x.at === ttime.dataset.tubTime)); if (o) { this.tubTime = { asap: !!o.asap, at: o.at, label: o.asap ? "ASAP" : o.label }; this.reflow(); document.querySelector(`[data-tub-time="${CSS.escape(ttime.dataset.tubTime)}"]`)?.focus(); } return; }
      if (this.onBoxClick(e)) return;
      const b = e.target.closest("button");
      if (!b) return;
      if (b.matches("[data-leave-show]")) { this.close("order-leave"); this.applyLeave(this.draft || []); }
      else if (b.matches("[data-pick-store]")) {
        const s = this.stores.find((x) => x.id === b.dataset.pickStore);
        if (s && s.open && s.open.web) {
          // From the chocolate stores' sheet, the page lands on Chocolate at the new store.
          const fromChoc = !!b.closest("#order-choc-stores");
          this.close("order-stores"); this.close("order-choc-stores");
          this.toChoc = fromChoc;
          this.choose(s, true);
        }
      } else if (b.matches("[data-pick-time]")) {
        const v = b.dataset.pickTime;
        const o = this.times.options.find((x) => (v === "asap" ? x.asap : x.at === v));
        this.time = { asap: !!o.asap, at: o.at, label: o.label };
        this.renderTime();
        document.dispatchEvent(new CustomEvent("ms:pickup-time", { detail: { storeId: this.store.id, date: this.date, at: this.time.at } }));
        this.close("order-time");
      } else if (b.matches("[data-pick-scoop]")) {
        const opt = this.counter.scoops.find((s) => s.productId === b.dataset.pickScoop);
        this.close("order-size");
        this.add(scoopLines(this.store.id, this.date, opt), opt.title, "scoop");
      } else if (b.matches("[data-pick-tub]")) {
        // The tub goes back to the + that opened the size sheet when it closes, not to this row, which is going away.
        const back = this.sheet("order-size")?.opener;
        this.close("order-size");
        this.openTub(b.dataset.pickTub, this.firstFlavour ? [this.firstFlavour] : [], back);
      } else if (b.matches("[data-pick-merch-size]")) {
        this.close("order-size");
        const m = this.groups.merch.find((x) => x.sizes.some((s) => s.productId === b.dataset.pickMerchSize));
        const s = m.sizes.find((x) => x.productId === b.dataset.pickMerchSize);
        this.add([{ storeId: this.store.id, productId: s.productId, date: this.date, quantity: 1 }], m.title, "merch", { size: s.size, imageUrl: m.imageUrl });
      } else if (b.matches("[data-tub-flavour]")) {
        if (b.getAttribute("aria-disabled") === "true") return;
        const id = b.dataset.tubFlavour;
        const i = this.tubPicked.indexOf(id);
        if (i >= 0) this.tubPicked.splice(i, 1);
        else if (this.tubPicked.length < this.tub.maxFlavours) this.tubPicked.push(id);
        this.tubParts = even(this.tubPicked.length);
        this.tubNote = null;
        this.renderTub();
        document.querySelector(`[data-tub-flavour="${CSS.escape(id)}"]`)?.focus();
      } else if (b.matches("[data-tub-even]")) { this.tubParts = even(this.tubPicked.length); this.renderTub(); }
      else if (b.matches("[data-tub-to-list]")) { const first = document.querySelector('[data-tub-list] [data-tub-flavour]:not([aria-disabled="true"])'); first?.scrollIntoView({ block: "center" }); first?.focus({ preventScroll: true }); }
      else if (b.matches("[data-add-tub]")) this.addTub();
    }

    openSizes(flavourId) {
      const all = [...(this.flavours?.specials || []), ...(this.flavours?.classics || [])];
      const f = all.find((x) => x.id === flavourId);
      this.firstFlavour = flavourId;
      const list = document.querySelector("[data-size-list]");
      document.querySelector("[data-size-sub]").textContent = f.title;
      const mark = this.icon("chevron-right");
      const row = (attr, id, dot, title, sub, cents, out) => `<button class="pick-row" type="button" ${attr}="${esc(id)}"${out ? " disabled" : ""}>
        <span class="size-dot" aria-hidden="true">${esc(dot)}</span><span style="flex: 1; text-align: left"><span class="t-card" style="display: block">${esc(title)}</span>
        <span class="t-small t-soft" style="display: block">${out ? "Sold out here" : esc(sub)}</span></span><b>${money(cents)}</b>${out ? "" : mark}</button>`;
      const tubs = this.pricedTubs().map((t) => row("data-pick-tub", t.productId, tubLabel(t.name), `${tubLabel(t.name)} tub`, `Up to ${t.maxFlavours} flavours, ${f.title} first`, t.priceCents, t.soldOut)).join("");
      const scoops = this.delivery ? "" : this.counter.scoops.map((s) => row("data-pick-scoop", s.productId, s.baby ? "B" : String(s.count), s.title, "Cup or cone", s.priceCents, s.soldOut)).join("");
      const title = document.querySelector("[data-size-title]");
      if (title) title.textContent = tubs && scoops ? "Tub or scoops?" : tubs ? "What size tub?" : "How many scoops?";
      list.innerHTML = (tubs ? `<div class="t-label pick-hd" role="heading" aria-level="3">Tubs</div>${tubs}` : "")
        + (scoops ? `<div class="t-label pick-hd" role="heading" aria-level="3">Scoops, ASAP today</div><p class="t-small t-soft" style="margin: 0 0 4px">Scoops are made at the counter, so you ask for ${esc(f.title)} there.</p>${scoops}` : "")
        + (this.delivery && this.counter.scoops.length ? `<p class="t-small t-soft" style="margin: 8px 0 0">Scoops are pick-up only.</p>` : "");
      this.open("order-size");
    }

    addMerch(key) {
      const m = this.groups.merch.find((x) => x.key === key);
      const sizes = m.sizes.filter((s) => s.size);
      if (sizes.length <= 1) { const s = m.sizes.find((x) => !x.soldOut); this.add([{ storeId: this.store.id, productId: s.productId, date: this.date, quantity: 1 }], m.title, "merch", { imageUrl: m.imageUrl }); return; }
      document.querySelector("[data-size-sub]").textContent = m.title;
      const title = document.querySelector("[data-size-title]");
      if (title) title.textContent = "What size?";
      document.querySelector("[data-size-list]").innerHTML = m.sizes.map((s) => `<button class="pick-row" type="button" data-pick-merch-size="${esc(s.productId)}"${s.soldOut ? " disabled" : ""}>
        <span style="flex: 1; text-align: left"><span class="t-card" style="display: block">${esc(s.size)}</span><span class="t-small t-soft" style="display: block">${s.soldOut ? "Sold out here" : `Picked up from ${esc(this.store.name)}`}</span></span><b>${money(s.priceCents)}</b></button>`).join("");
      this.open("order-size");
    }

    // ── A tub, as the app's tub screen ──────────────────────────────────────────────────────────────────────────

    openTub(productId, picked, opener) {
      this.sheetKind = "tub";
      this.tub = this.pricedTubs().find((t) => t.productId === productId);
      this.tubPicked = picked.slice(0, this.tub.maxFlavours);
      this.tubParts = even(this.tubPicked.length);
      this.tubDate = this.date;
      this.tubTime = this.time.asap ? { asap: true, at: null, label: "ASAP" } : { ...this.time };
      this.tubNote = null;
      this.renderTub();
      this.sheet("order-tub")?.open?.(opener);
    }

    tubItem() { return (this.items || []).find((i) => i.productId === (this.sheetKind === "box" ? this.box.productId : this.tub.productId)); }
    tubTimes() { return this.dayTimes && this.dayTimes[this.tubDate]; }

    async pickTubDay(date) {
      this.tubDate = date;
      if (!this.dayTimes[date]) {
        this.reflow();
        const r = await hub("pickup-times", { storeId: this.store.id, date });
        this.dayTimes[date] = r.ok ? dayTimes(r.data, this.store.name, this.zone, date === this.date) : null;
      }
      const t = this.tubTimes();
      this.tubTime = date === this.date ? { asap: true, at: null, label: "ASAP" } : t && t.options[0] ? { asap: false, at: t.options[0].at, label: t.options[0].label } : null;
      this.reflow();
      document.querySelector(`[data-tub-day="${date}"]`)?.focus();
    }

    /** The open sheet that picks a day and time: the box's, or the tub's. */
    reflow() { if (this.sheetKind === "box") this.renderBox(); else this.renderTub(); }

    changeSize(index) {
      const t = this.pricedTubs()[index];
      if (!t || t.productId === this.tub.productId || t.soldOut) { this.renderTub(); return; }
      if (this.tubPicked.length > t.maxFlavours) {
        this.tubNote = `A ${tubLabel(t.name)} tub takes ${t.maxFlavours} flavours. Take one out, then tap ${tubLabel(t.name)} again.`;
        this.renderTub();
        return;
      }
      this.tub = t;
      this.tubNote = null;
      this.renderTub();
    }

    renderTub() {
      const f = this.flavours || { specials: [], classics: [] };
      const all = [...f.specials, ...f.classics];
      const pic = (id) => all.find((x) => x.id === id);
      const max = this.tub.maxFlavours, n = this.tubPicked.length;
      const delivery = !!this.delivery;
      const later = !delivery && tubLater(this.tubDate, this.date, this.tubTime);
      const q = (sel) => document.querySelector(sel);
      q("[data-tub-title]").textContent = `${tubLabel(this.tub.name)} tub`;
      q("[data-tub-sub]").textContent = `Up to ${max} flavours · ${delivery ? `delivered from ${this.store.name}` : this.store.name}`;
      // An empty slot is a button that takes you down to the flavours.
      q("[data-tub-slots]").innerHTML = later ? "" : Array.from({ length: max }, (_, i) => { const x = pic(this.tubPicked[i]); return x ? `<button class="slot picked-slot" type="button" data-tub-flavour="${esc(this.tubPicked[i])}" aria-label="Take ${esc(x.title)} out">${x.imageUrl ? `<img src="${esc(x.imageUrl)}" alt="">` : ""}<span class="slot-x" aria-hidden="true">${this.icon("x")}</span></button>` : (i === this.tubPicked.length ? `<button class="slot add-slot" type="button" data-tub-to-list aria-label="Add a flavour">${this.icon("plus")}</button>` : `<span class="slot"></span>`); }).join("");
      q("[data-tub-count]").textContent = later ? "" : `${n} of ${max} flavours picked`;
      const sizes = this.pricedTubs();
      const on = sizes.findIndex((t) => t.productId === this.tub.productId);
      q("[data-tub-sizes]").innerHTML = `<div class="track full" role="radiogroup" aria-label="Tub size">${sizes.map((t, i) => `<span role="radio" tabindex="${i === on ? 0 : -1}" aria-checked="${i === on}" class="${i === on ? "on" : ""}"${t.soldOut ? ' aria-disabled="true"' : ""}>${esc(tubLabel(t.name))} · ${money(t.priceCents)}</span>`).join("")}</div>`;
      const note = this.tubNote || (later ? null : !n ? (delivery ? "Put a flavour in first." : "Put a flavour in, or pick a later day and choose them at the counter.") : n === max ? "Your tub’s full. Take one out to swap it." : null);
      const noteEl = q("[data-tub-note]");
      noteEl.hidden = !note; noteEl.innerHTML = note ? `${this.icon("info")}${esc(note)}` : "";
      // How much of each: the app's split, halves, thirds and quarters only; four or five go in evenly and lock.
      const names = this.tubPicked.map((id) => pic(id)?.title || "");
      let split = "";
      if (!later && n >= 2) {
        const parts = this.tubParts && this.tubParts.length === n ? this.tubParts : even(n);
        const fr = fractions(parts);
        const movable = canSplit(n);
        const total = parts.reduce((a, b) => a + b, 0);
        const handles = cuts(parts);
        const segs = parts.map((p, i) => (i ? (movable ? this.handleHtml(i - 1, parts, names, handles[i - 1], total) : `<span class="ts-lock" aria-hidden="true">${this.icon("lock")}</span>`) : "") + `<div class="ts-seg" style="flex: ${p}">${pic(this.tubPicked[i])?.imageUrl ? `<img src="${esc(pic(this.tubPicked[i]).imageUrl)}" alt="">` : ""}</div>`).join("");
        split = `<div class="ts-row"><div class="t-label-l ts-h" role="heading" aria-level="3">How much of each</div>${movable && !isEven(parts) ? `<button class="q-link" type="button" data-tub-even>Even it out</button>` : ""}</div>`
          + `<div class="ts-bar${movable ? "" : " locked"}" data-tub-bar>${segs}</div><div class="ts-legend" style="grid-template-columns: ${parts.map((p) => `${p}fr`).join(" ")}">${parts.map((p, i) => `<span><b>${fr[i]}</b>${esc(names[i])}</span>`).join("")}</div>`
          + `<p class="t-small t-soft" style="margin: 0">${movable ? (n === 2 ? "Slide the handle for a half, a third or a quarter." : "Slide a handle to give one flavour half. The other two get a quarter each.") : `${this.icon("lock")} Sorry, we can’t change the ratios because the tub is packed too tight with this many flavours.`}</p>`;
      }
      q("[data-tub-split]").innerHTML = split;
      const counter = q("[data-tub-counter]");
      counter.hidden = !later;
      if (later) q("[data-tub-counter-line]").textContent = `We pack every tub fresh and can’t keep one waiting, so ${this.tubDate === this.date ? "you choose your flavours" : `for ${dayWord(this.tubDate, this.date)} you choose your flavours`} when you get here. It’s already paid for.`;
      q("[data-tub-when]").innerHTML = this.whenHtml(delivery);
      q("[data-tub-list]").innerHTML = later ? "" : this.tubListHtml(f, max);
      const add = q("[data-add-tub]");
      const ready = later || tubReady(this.tubPicked, max);
      add.toggleAttribute("disabled", false);
      if (ready && this.tubTime !== null) { add.removeAttribute("aria-disabled"); add.removeAttribute("aria-describedby"); }
      else { add.setAttribute("aria-disabled", "true"); add.setAttribute("aria-describedby", "tub-note"); }
      add.querySelector("[data-add-label]").textContent = tubButton({ date: this.tubDate, today: this.date, time: this.tubTime, cents: this.tub.priceCents, delivery });
    }

    handleHtml(i, parts, names, at, total) {
      const fr = fractions(parts);
      const opts = splits(parts.length).map((s) => cuts(s)[i]);
      const words = { "1/2": "a half", "1/3": "a third", "2/3": "two thirds", "1/4": "a quarter", "3/4": "three quarters" };
      const said = (k) => words[fr[k]] || fr[k];
      const vt = `${said(i).charAt(0).toUpperCase() + said(i).slice(1)} ${names[i]}, ${said(i + 1)} ${names[i + 1]}`;
      return `<div class="ts-handle" role="slider" tabindex="0" data-cut="${i}" aria-label="Between ${esc(names[i])} and ${esc(names[i + 1])}" aria-valuemin="${Math.min(...opts)}" aria-valuemax="${Math.max(...opts)}" aria-valuenow="${at}" aria-valuetext="${esc(vt)}">${this.icon("grip-vertical")}</div>`;
    }

    whenHtml(delivery, noun = "tub") {
      if (delivery) {
        // Michael, 26 Oct 2026: boxes go by courier from stores that deliver, today and ASAP like the rest.
        const why = noun === "box" ? `Boxes go out ASAP today, by courier from ${esc(this.store.name)}.` : "Tubs are delivered ASAP because we want to pack them fresh.";
        return `<div class="t-label-l ts-h" role="heading" aria-level="3">When</div><p class="t-small line-info">${this.icon("truck")}${why}</p>`;
      }
      const days = tubDays(this.tubItem(), this.date);
      const list = days.length ? days : [{ date: this.date, d: "TODAY", n: Number(this.date.slice(8)) }];
      const chips = `<div class="tdays" role="radiogroup" aria-label="Pick-up day">${list.map((d) => `<span class="tday${d.date === this.tubDate ? " on" : ""}" role="radio" tabindex="${d.date === this.tubDate ? 0 : -1}" aria-checked="${d.date === this.tubDate}" aria-label="${esc(dayLong(d.date, this.date))}" data-tub-day="${d.date}"><span class="d">${d.d}</span><span class="n">${d.n}</span></span>`).join("")}</div>`;
      const t = this.tubTimes();
      let times;
      if (t === undefined) times = `<p class="t-small t-soft">Checking ${esc(this.store.name)}’s times…</p>`;
      else if (!t) times = `<p class="t-small t-soft">We can’t check ${esc(this.store.name)}’s pick-up times for that day right now.</p>`;
      else if (t.tooLate) times = `<p class="t-small">${esc(t.message)}</p>`;
      else {
        const on = (o) => (this.tubTime && ((o.asap && this.tubTime.asap) || (!o.asap && o.at === this.tubTime.at)));
        times = `<div class="pick-list" role="radiogroup" aria-label="Pick-up time">${t.options.map((o) => `<span class="pick-row${on(o) ? " on" : ""}" role="radio" tabindex="${on(o) ? 0 : -1}" aria-checked="${!!on(o)}" data-tub-time="${o.asap ? "asap" : esc(o.at)}"><span class="t-card" style="flex: 1">${esc(o.label)}</span>${on(o) ? this.icon("check") : ""}</span>`).join("")}</div>`;
      }
      if (noun === "box") times = `<div class="box-times">${times}</div>`;
      return `<div class="t-label-l ts-h" role="heading" aria-level="3">This ${noun}’s pick-up</div><p class="t-small t-soft when-line">${this.icon("map-pin")}Pick-up from ${esc(this.store.name)}</p>${chips}<div class="t-label-l ts-h" role="heading" aria-level="3">Time</div>${times}`
        + (noun === "tub" && this.tubDate === this.date ? `<p class="t-small t-soft" style="margin: 6px 0 0">Pick ASAP and your flavours go in now. Any later and you choose them at the counter, so we can pack it fresh.</p>` : "");
    }

    tubListHtml(f, max) {
      const left = leaveMenu(f, this.leaving);
      const full = this.tubPicked.length >= max;
      const row = (x) => {
        const on = this.tubPicked.includes(x.id);
        return `<div class="mrow cl-row"><div class="dot">${x.imageUrl ? `<img src="${esc(x.imageUrl)}" alt="" loading="lazy">` : ""}</div><div class="tx"><div class="t-card">${esc(x.title)}</div>${this.codesHtml(x)}</div>`
          + `<button class="circle ${on ? "picked" : "add-c"}" type="button" data-tub-flavour="${esc(x.id)}" aria-pressed="${on}" aria-label="${esc(x.title)}"${!on && full ? ' aria-disabled="true" aria-describedby="tub-note"' : ""}>${this.icon(on ? "check" : "plus")}</button></div>`;
      };
      return (left.specials.length ? `<div class="t-label-l ts-h" role="heading" aria-level="3">This month’s specials</div><div class="cyc-dot">${left.specials.map(row).join("")}</div>` : "")
        + (left.classics.length ? `<div class="t-label-l ts-h" role="heading" aria-level="3">Classics</div><div class="cyc-dot">${left.classics.map(row).join("")}</div>` : "");
    }

    /** The split's handles: dragged, or stepped with the arrow keys to the next allowed split. */
    onPointer(e) {
      const h = e.target.closest(".ts-handle[data-cut]");
      if (!h) return;
      e.preventDefault();
      const i = Number(h.dataset.cut);
      const move = (ev) => {
        const bar = document.querySelector("[data-tub-bar]");
        if (!bar) return;
        const r = bar.getBoundingClientRect();
        const next = moveCut(this.tubParts, i, Math.min(1, Math.max(0, (ev.clientX - r.left) / r.width)));
        if (next.join() !== this.tubParts.join()) { this.tubParts = next; this.renderTub(); }
      };
      const up = () => { document.removeEventListener("pointermove", move); document.removeEventListener("pointerup", up); document.querySelector(`.ts-handle[data-cut="${i}"]`)?.focus(); };
      document.addEventListener("pointermove", move);
      document.addEventListener("pointerup", up);
    }

    onKey(e) {
      const h = e.target.closest?.(".ts-handle[data-cut]");
      if (!h) return;
      const step = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }[e.key];
      if (!step) return;
      e.preventDefault();
      const i = Number(h.dataset.cut);
      this.tubParts = stepCut(this.tubParts, i, step);
      this.renderTub();
      document.querySelector(`.ts-handle[data-cut="${i}"]`)?.focus();
    }

    addTub() {
      const add = document.querySelector("[data-add-tub]");
      if (add.getAttribute("aria-disabled") === "true") return;
      const delivery = !!this.delivery;
      const later = !delivery && tubLater(this.tubDate, this.date, this.tubTime);
      if (!later && !tubReady(this.tubPicked, this.tub.maxFlavours)) return;
      this.close("order-tub");
      const all = [...(this.flavours?.specials || []), ...(this.flavours?.classics || [])];
      const date = delivery ? this.date : this.tubDate;
      if (!delivery && this.tubTime && !this.tubTime.asap) document.dispatchEvent(new CustomEvent("ms:pickup-time", { detail: { storeId: this.store.id, date, at: this.tubTime.at } }));
      const picked = later ? [] : this.tubPicked;
      const imgs = picked.map((id) => all.find((f) => f.id === id)?.imageUrl || null);
      this.add([tubLine(this.store.id, date, this.tub, this.tubPicked, this.tubParts, later)], `${tubLabel(this.tub.name)} tub`, "tub",
        { flavourNames: picked.map((id) => all.find((f) => f.id === id)?.title || ""), imageUrl: imgs[0] || null, flavourImages: imgs.length > 1 ? imgs : null });
    }

    // ── Chocolate: the band, a box, a bar (dev/canvas/pages_drops.py orderchoc) ─────────────────────────────────

    /** The band: the store's boxes, picked up any day or in a delivery today; at a store without boxes the stores that
     *  have them; leaving out what every bar has, it says so. Hidden when nothing's known about chocolate. */
    renderChoc() {
      const sec = this.$("[data-group='chocolate']");
      if (!sec) return;
      const boxes = boxesOf(this.items, this.date);
      this.boxes = boxes;
      const lead = this.$("[data-choc-lead]"), btns = this.$("[data-choc-btns]"), list = this.$("[data-boxes]");
      const brand = this.$("[data-choc-words]")?.innerHTML.trim() || "";
      btns.innerHTML = ""; list.innerHTML = "";
      const others = chocStores(this.across);
      if (!boxes.length) {
        sec.hidden = !others.length || this.cfg.only === "tubs";
        if (sec.hidden) return;
        const names = others.flatMap((g) => g.stores);
        lead.textContent = `Chocolate boxes are made up at ${andList(names)}. ${this.store.name} doesn’t have them.`;
        btns.innerHTML = `<button class="btn pista" type="button" data-sheet-open="order-choc-stores" aria-haspopup="dialog">${this.icon("map-pin")}Choose a store</button>`;
        this.renderChocStores();
        return;
      }
      sec.hidden = this.cfg.only === "tubs";
      const kept = barsFor(boxes[0].bars, this.leaving);
      if (this.leaving.length && !kept.on.length && !kept.off.length) {
        lead.textContent = noneLeft("our bars", this.leaving);
        const words = leaveLine(this.leaving) || "";
        const stop = /^Leaving out /.test(words) ? `Stop ${words.charAt(0).toLowerCase()}${words.slice(1)}` : "Show every bar";
        btns.innerHTML = `<button class="btn pista" type="button" data-leave-clear>${this.icon("x")}${esc(stop)}</button>`;
        return;
      }
      lead.innerHTML = `${brand ? `${brand} ` : ""}Choose a box, fill it with ${esc(this.store.name)}’s bars, and ${this.delivery ? "we’ll deliver it today." : "pick it up any day."}`;
      list.innerHTML = boxes.map((b) => {
        const off = b.soldOut || this.closed ? " disabled" : ` aria-haspopup="dialog"`;
        const sub = b.soldOut ? "Sold out here" : `Pick any ${b.pick} bars`;
        return `<button class="tub-size" type="button" data-box="${esc(b.productId)}"${off} aria-label="${esc(b.title)}, ${money(b.priceCents)}, ${esc(sub.toLowerCase())}">`
          + `<span class="t-title">${esc(b.title)}</span><span class="tub-price">${money(b.priceCents)}</span><span class="t-small">${esc(sub)}</span>${b.soldOut ? "" : `<span class="ts-add" aria-hidden="true">${this.icon("plus")}</span>`}</button>`;
      }).join("");
    }

    /** The sheet a store without boxes opens: the stores that have them, by state; picking one moves the order there. */
    renderChocStores() {
      const box = document.querySelector("[data-choc-stores]");
      if (!box) return;
      const groups = chocStores(this.across);
      const n = groups.reduce((t, g) => t + g.stores.length, 0);
      const sub = document.querySelector("[data-choc-stores-sub]");
      if (sub) sub.textContent = `Chocolate boxes are made up at ${n === 1 ? "this store" : `these ${NUMBER_WORDS[n] || n} stores`}. Pick one and it’s your store for the whole order.`;
      const byName = new Map((this.across?.stores || []).map((s) => [s.name, s.storeId]));
      box.innerHTML = groups.map((g) => `<div class="t-label pick-hd" role="heading" aria-level="3">${esc(g.state)}</div>` + g.stores.map((name) =>
        `<button class="pick-row" type="button" data-pick-store="${esc(byName.get(name))}"><span class="t-card" style="flex: 1; text-align: left">${esc(name)}</span>${this.icon("chevron-right")}</button>`).join("")).join("");
    }

    /** A link that asks for a box (?box=4, and a bar by its page, ?bar=lamington: the cart's and a bar page's), once. */
    openAsked() {
      if (this.askedDone) return;
      const q = new URLSearchParams(location.search);
      const pick = Number(q.get("box"));
      if (!pick) return;
      this.askedDone = true;
      const b = (this.boxes || []).find((x) => x.pick === pick && !x.soldOut);
      if (!b) { document.getElementById("order-chocolate")?.scrollIntoView({ block: "start" }); return; }
      const bar = b.bars.find((x) => x.handle && x.handle === q.get("bar") && x.available === true);
      this.openBox(b.productId, bar ? [bar.id] : [], this.$(`[data-box="${CSS.escape(b.productId)}"]`));
    }

    openBox(productId, picked, opener, note = null) {
      this.sheetKind = "box";
      this.box = (this.boxes || []).find((b) => b.productId === productId);
      if (!this.box) return;
      this.boxPicked = picked.slice(0, this.box.pick);
      this.boxNote = note;
      this.tubDate = this.date;
      this.tubTime = this.delivery || this.time.asap ? { asap: true, at: null, label: "ASAP" } : { ...this.time };
      this.renderBox();
      this.sheet("order-box")?.open?.(opener);
      // Focus starts on the box's name, as the board has it.
      const title = document.getElementById("order-box-title");
      if (title) { title.tabIndex = -1; title.focus(); }
    }

    boxBar(id) { return this.box.bars.find((b) => b.id === id); }

    renderBox() {
      const b = this.box, q = (sel) => document.querySelector(sel);
      const picked = this.boxPicked, words = trayWords(picked, b.pick, b.bars);
      const later = this.tubDate !== this.date;
      q("[data-box-title]").textContent = b.title;
      q("[data-box-sub]").textContent = `${money(b.priceCents)} · ${this.delivery ? `delivered from ${this.store.name}` : this.store.name}`;
      const pic = (bar) => (bar && bar.imageUrl ? `<img src="${esc(bar.imageUrl)}" alt="">` : `<span class="bar-init" aria-hidden="true">${esc((bar?.title || "?").charAt(0))}</span>`);
      const slots = Array.from({ length: b.pick }, (_, i) => {
        const id = picked[i];
        if (!id) return `<span class="slot"></span>`;
        const bar = this.boxBar(id);
        return `<button class="slot picked-slot" type="button" data-box-take="${i}" aria-label="Take a ${esc(bar?.title || "bar")} out"><span class="slot-pic">${pic(bar)}</span><span class="slot-x" aria-hidden="true">${this.icon("x")}</span></button>`;
      }).join("");
      const go = words.left > 0 ? `<a class="q-link" href="#box-bars" data-box-to-bars>${this.icon("chevron-down")}Go to the bars</a>` : "";
      const note = this.boxNote ? `<p class="t-small line-info" id="box-note" tabindex="-1" style="margin: 0">${this.icon("info")}${esc(this.boxNote)}</p>` : "";
      q("[data-box-hd]").innerHTML = `<div class="tub-hd box-hd"><div class="tub-slots">${slots}</div><p class="t-small tub-count" role="status" id="box-left" tabindex="-1"><span aria-hidden="true">${esc(words.seen)}</span><span class="sr">${esc(words.said)}</span></p>${go}</div>${note}`;
      q("[data-box-when]").innerHTML = this.whenHtml(!!this.delivery, "box");
      const bars = barsFor(b.bars, this.leaving);
      const full = words.left <= 0;
      const row = (bar, on, i) => {
        const n = barCount(picked, bar.id);
        // In a delivery, naming another store would send them to one that may not deliver, so it's left out.
        const has = on || this.delivery ? null : hasIt(bar.id, this.store, this.across);
        const facts = on ? (Array.isArray(bar.allergens) ? this.barCodes(bar.allergens) : `<span class="codes ask">Allergens: ask at the counter</span>`)
          : `<span class="sold-line">${this.icon("x")}Sold out here</span>${has ? `<span class="has-line">${this.icon("store")}${esc(has)} has it</span>` : ""}`;
        const t = esc(bar.title);
        let right;
        if (!on) right = `<button class="circle add-c" type="button" aria-disabled="true" aria-label="Add ${t}, sold out here">${this.icon("plus")}</button>`;
        else if (n) right = `<div class="vstep mini" role="group" aria-label="${t}"><button class="circle" type="button" data-box-less="${esc(bar.id)}" aria-label="One fewer ${t}">${this.icon("minus")}</button><span class="vcount" aria-hidden="true">${n}</span>`
          + `<button class="circle add" type="button" data-box-more="${esc(bar.id)}" aria-label="One more ${t}, ${n} in${full ? ", your box is full" : ""}"${full ? ' aria-disabled="true"' : ""}>${this.icon("plus")}</button></div>`;
        else right = `<button class="circle add-c" type="button" data-box-more="${esc(bar.id)}" aria-label="Add ${t}${full ? ", your box is full" : ""}"${full ? ' aria-disabled="true"' : ""}>${this.icon("plus")}</button>`;
        return `<li class="mrow bar-row${on ? "" : " shut"}"><button class="bar-head" type="button" aria-haspopup="dialog" data-bar-info="${esc(bar.id)}"><span class="dot${on ? "" : " dim"}">${pic(bar)}</span><span class="tx"><span class="t-card">${t}</span>${facts}</span></button>${right}</li>`;
      };
      const rows = bars.on.map((x, i) => row(x, true, i)).join("") + bars.off.map((x, i) => row(x, false, bars.on.length + i)).join("");
      const day = later ? dayLong(this.tubDate, this.date).split(" ")[0] : null;
      q("[data-box-bars]").innerHTML = `<div class="t-label-l ts-h" role="heading" aria-level="3" id="box-bars" tabindex="-1">${esc(this.store.name)}’s bars today</div>`
        + `<p class="t-small t-soft" style="margin: 0 0 6px">The same one twice is fine. Tap a bar for what’s in it.${day ? ` For ${esc(day)}, we set your bars aside as soon as your order comes in.` : ""}</p>`
        + (rows ? `<ul class="cyc-dot bar-list" role="list">${rows}</ul>` : `<p class="t-small">${esc(noneLeft("our bars", this.leaving))}</p>`);
      const add = q("[data-add-box]");
      add.querySelector("[data-add-label]").textContent = boxButton({ left: words.left, pick: b.pick, fresh: !picked.length, date: this.tubDate, today: this.date, time: this.tubTime, cents: b.priceCents });
      if (words.left > 0 || this.tubTime === null) { add.setAttribute("aria-disabled", "true"); add.setAttribute("aria-describedby", "box-left"); }
      else { add.removeAttribute("aria-disabled"); add.removeAttribute("aria-describedby"); }
    }

    /** A bar's codes as the menu's flavours have them, inside its name's button (so spans). */
    barCodes(codes) {
      const t = codesText(codes);
      return t ? `<span class="codes"><span aria-hidden="true">${esc(t)}</span><span class="sr">${esc(codesSaid(codes) || "")}</span></span>` : "";
    }

    /** The box sheet's and the bar sheet's buttons; true when it was one of theirs. */
    onBoxClick(e) {
      const b = e.target.closest("button, a");
      if (!b || !b.closest("#order-box, #order-bar")) return false;
      const box = this.box;
      if (b.matches("[data-box-more]")) {
        if (b.getAttribute("aria-disabled") === "true") return true;
        const id = b.dataset.boxMore;
        if (this.boxPicked.length < box.pick) this.boxPicked.push(id);
        this.boxNote = null;
        this.renderBox();
        document.querySelector(`#order-box [data-box-more="${CSS.escape(id)}"]`)?.focus();
        return true;
      }
      if (b.matches("[data-box-less]")) {
        const id = b.dataset.boxLess;
        const i = this.boxPicked.lastIndexOf(id);
        if (i >= 0) this.boxPicked.splice(i, 1);
        this.renderBox();
        // Down to none, focus goes to its Add.
        (document.querySelector(`#order-box [data-box-less="${CSS.escape(id)}"]`) || document.querySelector(`#order-box [data-box-more="${CSS.escape(id)}"]`))?.focus();
        return true;
      }
      if (b.matches("[data-box-take]")) {
        const i = Number(b.dataset.boxTake);
        this.boxPicked.splice(i, 1);
        this.renderBox();
        (document.querySelector(`#order-box [data-box-take="${Math.min(i, this.boxPicked.length - 1)}"]`) || document.getElementById("box-left"))?.focus();
        return true;
      }
      if (b.matches("[data-box-to-bars]")) { e.preventDefault(); this.toBars(); return true; }
      if (b.matches("[data-add-box]")) { if (b.getAttribute("aria-disabled") === "true") this.toBars(); else this.addBox(); return true; }
      if (b.matches("[data-bar-info]")) { this.openBar(b.dataset.barInfo, b); return true; }
      if (b.matches("[data-bar-add]")) {
        const id = this.barShown;
        this.close("order-bar");
        if (id && this.boxPicked.length < box.pick && this.boxBar(id)?.available === true) { this.boxPicked.push(id); this.renderBox(); }
        document.querySelector(`#order-box [data-box-more="${CSS.escape(id)}"]`)?.focus();
        return true;
      }
      return false;
    }

    toBars() { const h = document.getElementById("box-bars"); h?.scrollIntoView({ block: "start" }); h?.focus({ preventScroll: true }); }

    /** A bar's own sheet over the box's: its photo, its page's words and allergens, and Add to the box. */
    async openBar(id, opener) {
      const bar = this.boxBar(id);
      if (!bar) return;
      this.barShown = id;
      const q = (sel) => document.querySelector(sel);
      q("[data-bar-title]").textContent = bar.title;
      q("[data-bar-sub]").textContent = bar.available === true ? `In ${this.store.name}’s boxes today` : `Sold out at ${this.store.name} today`;
      const circle = `<div class="bar-big"><span class="dot">${bar.imageUrl ? `<img src="${esc(bar.imageUrl)}" alt="">` : `<span class="bar-init" aria-hidden="true">${esc(bar.title.charAt(0))}</span>`}</span></div>`;
      const said = Array.isArray(bar.allergens) ? codesSaid(bar.allergens) : null;
      const allergens = `<p class="t-small" style="margin: 0"><b>Allergens:</b> ${said ? esc(said.replace(/^Contains /, "")) : "ask at the counter."}</p>`;
      const body = q("[data-bar-body]");
      body.innerHTML = `${circle}<p class="t-body" data-bar-words style="margin: 0">${bar.handle ? "" : "We haven’t written this one up yet."}</p>${allergens}`;
      const add = q("[data-bar-add]");
      const full = this.boxPicked.length >= this.box.pick;
      add.hidden = bar.available !== true;
      if (full) { add.setAttribute("aria-disabled", "true"); add.querySelector("span").textContent = "Your box is full"; }
      else { add.removeAttribute("aria-disabled"); add.querySelector("span").textContent = "Add to the box"; }
      this.sheet("order-bar")?.open?.(opener);
      if (!bar.handle) return;
      try {
        const r = await fetch(`/products/${encodeURIComponent(bar.handle)}.js`, { credentials: "same-origin" });
        const p = r.ok ? await r.json() : null;
        const words = p && p.description ? new DOMParser().parseFromString(p.description, "text/html").body.textContent.trim().replace(/\s*Allergens:.*$/is, "") : "";
        const w = body.querySelector("[data-bar-words]");
        if (w && this.barShown === id) w.textContent = words;
      } catch { /* the circle and allergens stand on their own */ }
    }

    addBox() {
      const b = this.box;
      if (this.boxPicked.length !== b.pick || this.tubTime === null) return;
      this.close("order-box");
      const date = this.delivery ? this.date : this.tubDate;
      if (!this.delivery && this.tubTime && !this.tubTime.asap) document.dispatchEvent(new CustomEvent("ms:pickup-time", { detail: { storeId: this.store.id, date, at: this.tubTime.at } }));
      const line = boxLine(this.store.id, date, b, this.boxPicked);
      this.add([line], b.title, "chocolate", { barNames: line.bars.map((id) => this.boxBar(id)?.title || ""), imageUrl: b.imageUrl });
    }

    /** A box the hub wouldn't hold because a bar has gone: it opens again with that bar taken out, and says so. */
    boxRefused({ line, answer }) {
      if (!line || !line.bars || !this.boxes || !["bar-sold-out", "bar-unknown", "bar-unavailable"].includes(answer && answer.reason)) return;
      const b = this.boxes.find((x) => x.productId === line.productId);
      if (!b) return;
      const gone = b.bars.find((x) => x.title === answer.bar);
      const kept = gone ? line.bars.filter((id) => id !== gone.id) : line.bars;
      if (gone) gone.available = false;
      this.openBox(b.productId, kept, this.$(`[data-box="${CSS.escape(b.productId)}"]`), `${answer.bar || "One of your bars"} is sold out here now. Swap it for another.`);
      document.getElementById("box-note")?.focus();
    }

    add(lines, title, kind, more = {}) {
      for (const line of lines) document.dispatchEvent(new CustomEvent("ms:added", { detail: { line, title, store: this.store, kind, ...more } }));
    }
  }
  if (!customElements.get("ms-order-menu")) customElements.define("ms-order-menu", MsOrderMenu);
}
